// Import from 'lit' (not 'lit-element') to match kerika-pwa.js — mixing the
// two installed packages renders templates as "[object Object]".
import { html, css } from 'lit';

import { store } from '../store.js';

import * as app from '../redux/app/index.js';
import * as board from '../redux/board/index.js';

import { goBack, isAtAppRoot } from '../mobile-back.js';
import { EDGE_SWIPE_PX, isInHorizontalScroller } from './utils.js';

import './kerika-icon.js';

/**
 * Back swipe from the screen's left edge for the iOS app, wired once on
 * [kerika-pwa](../kerika-pwa.js). iOS gives the installed app no back gesture of its own; Android has its
 * `backbutton` and the browsers theirs. Released past the threshold, it runs the app's common back
 * (`goBack()`), so a dialog or menu on top closes first, then the AI Helper, the card, the page.
 */

// finger travel before the swipe's direction is read
const DIRECTION_LOCK_PX = 10;

// shown = finger moved * dragResistance
const DRAG_RESISTANCE = 0.5;

// release past this shown distance to go back
const ACTIVATION_THRESHOLD_PX = 48;

// indicator stops following finger past this
const MAX_SHOWN_PX = 72;

// shown px where the indicator reaches full opacity
const OPACITY_FULL_PX = 20;

// indicator width; parked flush left of the viewport
const INDICATOR_PARK_PX = 40;

/**
 * @returns {Boolean} `true` where the edge swipe goes back: the iOS app in mobile layout.
 */
export const isEdgeSwipeBackEnabled = (state) => app.selectors.isiOS(state) && app.selectors.isInstalledApp(state) && !app.selectors.wideLayout(state);

export const edgeSwipeBackMixin = (baseElement) => class extends baseElement {
  static get styles() {
    return [
      super.styles || [],
      css`
        .edge-swipe-back-indicator {
          align-items: center;
          background-color: var(--mdc-theme-surface, #fff);
          border-radius: 50%;
          box-shadow:
            0 3px 6px rgba(0, 0, 0, 0.16),
            0 3px 6px rgba(0, 0, 0, 0.23);
          display: flex;
          height: 40px;
          justify-content: center;
          left: 0;
          opacity: 0;
          pointer-events: none;
          position: fixed;
          top: 50%;
          transform: translateX(-40px) translateY(-50%);
          transition: transform 200ms ease-out, opacity 200ms ease-out, background-color 150ms;
          width: 40px;
          will-change: transform, opacity;
          z-index: 10000;
        }

        .edge-swipe-back-indicator[dragging] {
          transition: background-color 150ms;
        }

        .edge-swipe-back-indicator kerika-icon {
          --dw-icon-color: var(--primary-color);
          --dw-icon-size: 24px;
        }

        .edge-swipe-back-indicator[ready] {
          background-color: var(--primary-color);
        }

        .edge-swipe-back-indicator[ready] kerika-icon {
          --dw-icon-color: var(--mdc-theme-on-primary, #fff);
        }
      `,
    ];
  }

  constructor() {
    super();
    this._boundEdgeBackTouchStart = this._edgeBackOnTouchStart.bind(this);
    this._boundEdgeBackTouchMove = this._edgeBackOnTouchMove.bind(this);
    this._boundEdgeBackTouchEnd = this._edgeBackOnTouchEnd.bind(this);
    this._boundEdgeBackTouchCancel = this._edgeBackOnTouchCancel.bind(this);

    // Non-reactive — the indicator is written imperatively from _edgeBackUpdateIndicator().
    this._edgeBackGesture = null;
  }

  connectedCallback() {
    super.connectedCallback && super.connectedCallback();
    // On `document`, as dialogs portaled to `document.body` are siblings of `kerika-pwa`. Touch events, not
    // pointer events: iOS keeps sending them while it scrolls, where a pointer gets a `pointercancel`.
    document.addEventListener('touchstart', this._boundEdgeBackTouchStart, { capture: true, passive: true });
    document.addEventListener('touchend', this._boundEdgeBackTouchEnd);
    document.addEventListener('touchcancel', this._boundEdgeBackTouchCancel);
  }

  disconnectedCallback() {
    super.disconnectedCallback && super.disconnectedCallback();
    document.removeEventListener('touchstart', this._boundEdgeBackTouchStart, { capture: true, passive: true });
    document.removeEventListener('touchend', this._boundEdgeBackTouchEnd);
    document.removeEventListener('touchcancel', this._boundEdgeBackTouchCancel);
    this._edgeBackReset();
  }

  /** Static template; visual state is written imperatively from _edgeBackUpdateIndicator(). @protected */
  get _edgeSwipeBackIndicatorTemplate() {
    return html`
      <div class="edge-swipe-back-indicator" aria-hidden="true">
        <kerika-icon name="arrow_back"></kerika-icon>
      </div>
    `;
  }

  /**
   * @returns {Boolean} `true` when this touch may start a back swipe: edge swipe enabled, no card drag, somewhere to go back to,
   * and the touch is not selecting text or scrolling content sideways.
   * @private
   */
  _isEdgeBackAllowed(event) {
    const state = store.getState();
    const target = event.composedPath()[0];
    const isOnEditable = target.isContentEditable || ['INPUT', 'TEXTAREA'].includes(target.tagName);
    return isEdgeSwipeBackEnabled(state) && !board.selectors.dragStarted(state) && !isAtAppRoot()
      && !isOnEditable && !document.getSelection()?.toString() && !isInHorizontalScroller(event);
  }

  _edgeBackOnTouchStart(event) {
    // A second finger cancels the swipe.
    if (event.touches.length !== 1) {
      this._edgeBackReset();
      return;
    }
    const touch = event.touches[0];
    if (touch.clientX > EDGE_SWIPE_PX || !this._isEdgeBackAllowed(event)) {
      return;
    }
    this._edgeBackGesture = { identifier: touch.identifier, startX: touch.clientX, startY: touch.clientY, isActive: false, shown: 0 };
    // Non-passive only while an edge touch is down, so every other scroll stays off the main thread.
    document.addEventListener('touchmove', this._boundEdgeBackTouchMove, { capture: true, passive: false });
  }

  _edgeBackOnTouchMove(event) {
    const gesture = this._edgeBackGesture;
    const touch = gesture && [...event.changedTouches].find((changedTouch) => changedTouch.identifier === gesture.identifier);
    if (!touch) {
      return;
    }
    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;
    if (!gesture.isActive) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < DIRECTION_LOCK_PX) {
        return;
      }
      // Only a rightward, mostly horizontal move is a back swipe; anything else stays the page's.
      // A card drag that started with a long press keeps its touch.
      if (deltaX <= 0 || Math.abs(deltaY) >= Math.abs(deltaX) || board.selectors.dragStarted(store.getState())) {
        this._edgeBackReset();
        return;
      }
      gesture.isActive = true;
    }
    // The swipe owns the touch now: no scroll under it, and the column carousels (`dw-swipe`, 25px restraint) never see it move.
    event.preventDefault();
    event.stopPropagation();
    gesture.shown = Math.max(0, Math.min(deltaX * DRAG_RESISTANCE, MAX_SHOWN_PX));
    this._edgeBackUpdateIndicator(gesture.shown, true, touch.clientY);
  }

  _edgeBackOnTouchEnd(event) {
    const gesture = this._edgeBackGesture;
    if (!gesture || ![...event.changedTouches].some((changedTouch) => changedTouch.identifier === gesture.identifier)) {
      return;
    }
    const shouldGoBack = gesture.isActive && gesture.shown >= ACTIVATION_THRESHOLD_PX;
    this._edgeBackReset();
    shouldGoBack && goBack();
  }

  _edgeBackOnTouchCancel() {
    this._edgeBackReset();
  }

  _edgeBackReset() {
    this._edgeBackGesture = null;
    document.removeEventListener('touchmove', this._boundEdgeBackTouchMove, { capture: true });
    this._edgeBackUpdateIndicator(0, false);
  }

  /**
   * Writes transform/opacity directly — no Lit render per touchmove.
   * @param {Number} [fingerY] The finger's height; the arrow sits there. Left out, the arrow keeps its height while it hides.
   * @private
   */
  _edgeBackUpdateIndicator(shown, dragging, fingerY) {
    const el = this.renderRoot && this.renderRoot.querySelector('.edge-swipe-back-indicator');
    if (!el) {
      return;
    }
    if (fingerY !== undefined) {
      // Kept whole on screen near the top and bottom.
      const halfHeight = INDICATOR_PARK_PX / 2;
      el.style.top = `${Math.max(halfHeight, Math.min(fingerY, window.innerHeight - halfHeight))}px`;
    }
    el.toggleAttribute('dragging', dragging);
    el.toggleAttribute('ready', shown >= ACTIVATION_THRESHOLD_PX);
    el.style.opacity = shown ? String(Math.min(shown / OPACITY_FULL_PX, 1)) : '';
    el.style.transform = shown ? `translateX(${shown - INDICATOR_PARK_PX}px) translateY(-50%)` : '';
  }
};
